import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, push, remove, set } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import './DateIdeas.css';

const DateIdeas = () => {
  const navigate = useNavigate();
  const { userProfile, currentUser } = useAuth();
  const [ideas, setIdeas] = useState({});
  const [newIdea, setNewIdea] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Real-time sync of date ideas from Firebase
  useEffect(() => {
    const ideasRef = ref(database, 'dateIdeas');

    const unsubscribe = onValue(ideasRef, (snapshot) => {
      if (snapshot.exists()) {
        setIdeas(snapshot.val());
      } else {
        setIdeas({});
      }
    });

    return () => unsubscribe();
  }, []);

  const handleAddIdea = async (e) => {
    e.preventDefault();
    if (!newIdea.trim()) return;

    try {
      const ideasRef = ref(database, 'dateIdeas');
      const newIdeaRef = push(ideasRef);

      await set(newIdeaRef, {
        text: newIdea.trim(),
        description: newDescription.trim() || null,
        createdBy: userProfile?.name || currentUser?.email || 'anonymous',
        createdByEmail: currentUser?.email || 'anonymous',
        createdAt: new Date().toISOString(),
        completed: false,
      });

      setNewIdea('');
      setNewDescription('');
      setIsAdding(false);
    } catch (error) {
      console.error('Error adding date idea:', error);
    }
  };

  const handleToggleComplete = async (ideaId, currentStatus) => {
    try {
      const ideaRef = ref(database, `dateIdeas/${ideaId}`);
      await set(ideaRef, {
        ...ideas[ideaId],
        completed: !currentStatus,
        completedAt: !currentStatus ? new Date().toISOString() : null,
      });
    } catch (error) {
      console.error('Error toggling date idea:', error);
    }
  };

  const handleDeleteIdea = async (ideaId) => {
    try {
      const ideaRef = ref(database, `dateIdeas/${ideaId}`);
      await remove(ideaRef);
    } catch (error) {
      console.error('Error deleting date idea:', error);
    }
  };

  // Sort ideas: incomplete first, then by creation date
  const sortedIdeas = Object.entries(ideas).sort(([, a], [, b]) => {
    if (a.completed !== b.completed) {
      return a.completed ? 1 : -1;
    }
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  return (
    <div className="date-ideas-page">
      {/* Gazebo background image */}
      <div
        className="gazebo-bg"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/gazebo.jpg)` }}
      />

      {/* Tape corners for gazebo */}
      <div className="gazebo-tape-corners">
        <div className="tape top-left" />
        <div className="tape top-right" />
        <div className="tape bottom-left" />
        <div className="tape bottom-right" />
      </div>

      {/* Decorative background elements */}
      <div className="date-ideas-bg">
        <div className="sparkle-field">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="bg-sparkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
              }}
            />
          ))}
        </div>
        <div className="flower-decoration flower-top-left" />
        <div className="flower-decoration flower-top-right" />
        <div className="flower-decoration flower-bottom-left" />
        <div className="flower-decoration flower-bottom-right" />
      </div>

      {/* Back button */}
      <motion.button
        className="back-button"
        onClick={() => navigate('/home')}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path
            d="M15 18l-6-6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Back to Desk
      </motion.button>

      {/* Main content container */}
      <motion.div
        className="date-ideas-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header */}
        <div className="date-ideas-header">
          <h1 className="date-ideas-title">Date Ideas</h1>
          <p className="date-ideas-subtitle">
            A bouquet of adventures waiting to happen
          </p>
        </div>

        {/* Ideas list */}
        <div className="ideas-list-container">
          <AnimatePresence mode="popLayout">
            {sortedIdeas.map(([id, idea]) => (
              <motion.div
                key={id}
                className={`idea-card ${idea.completed ? 'completed' : ''}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8, x: -100 }}
                layout
              >
                <button
                  className="idea-checkbox"
                  onClick={() => handleToggleComplete(id, idea.completed)}
                  aria-label={idea.completed ? 'Mark as incomplete' : 'Mark as complete'}
                >
                  {idea.completed ? (
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <circle cx="12" cy="12" r="10" fill="#c8e6c9" stroke="#81c784" strokeWidth="2" />
                      <path d="M8 12l3 3 5-6" stroke="#4caf50" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <circle cx="12" cy="12" r="10" fill="#fff9e6" stroke="#e8d4a0" strokeWidth="2" />
                    </svg>
                  )}
                </button>
                <div className="idea-content">
                  <p className="idea-text">{idea.text}</p>
                  {idea.description && (
                    <p className="idea-description">{idea.description}</p>
                  )}
                  <span className="idea-author">
                    added by {idea.createdBy}
                    {idea.completed && ' • completed!'}
                  </span>
                </div>
                <button
                  className="idea-delete"
                  onClick={() => handleDeleteIdea(id)}
                  aria-label="Delete idea"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Empty state */}
          {sortedIdeas.length === 0 && !isAdding && (
            <motion.div
              className="empty-state"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="empty-flower">
                <svg viewBox="0 0 80 80" width="80" height="80">
                  <g transform="translate(40, 40)">
                    <circle cx="0" cy="-14" r="10" fill="#ffe066" opacity="0.6" />
                    <circle cx="12" cy="-7" r="10" fill="#ffe066" opacity="0.6" />
                    <circle cx="12" cy="7" r="10" fill="#ffe066" opacity="0.6" />
                    <circle cx="0" cy="14" r="10" fill="#ffe066" opacity="0.6" />
                    <circle cx="-12" cy="7" r="10" fill="#ffe066" opacity="0.6" />
                    <circle cx="-12" cy="-7" r="10" fill="#ffe066" opacity="0.6" />
                    <circle cx="0" cy="0" r="8" fill="#ffb347" opacity="0.7" />
                  </g>
                </svg>
              </div>
              <p>No date ideas yet!</p>
              <p className="empty-hint">Grow your first adventure</p>
            </motion.div>
          )}
        </div>

        {/* Add new idea section */}
        <div className="add-idea-section">
          <AnimatePresence mode="wait">
            {isAdding ? (
              <motion.form
                key="form"
                className="add-idea-form"
                onSubmit={handleAddIdea}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <input
                  type="text"
                  className="idea-title-input"
                  placeholder="Date idea title..."
                  value={newIdea}
                  onChange={(e) => setNewIdea(e.target.value)}
                  autoFocus
                />
                <textarea
                  className="idea-input"
                  placeholder="Add a description (optional)..."
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  rows={3}
                />
                <div className="form-buttons">
                  <motion.button
                    type="button"
                    className="cancel-btn"
                    onClick={() => {
                      setIsAdding(false);
                      setNewIdea('');
                      setNewDescription('');
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="submit"
                    className="submit-btn"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={!newIdea.trim()}
                  >
                    Add Idea
                  </motion.button>
                </div>
              </motion.form>
            ) : (
              <motion.button
                key="button"
                className="add-idea-btn"
                onClick={() => setIsAdding(true)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <circle cx="12" cy="12" r="10" fill="#fff9e6" stroke="#e8d4a0" strokeWidth="2" />
                  <path d="M12 8v8M8 12h8" stroke="#c9a86c" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Add a new date idea
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default DateIdeas;
